/**
 * Configuration options for cleanFilters
 */
interface CleanFiltersOptions {
    /** Remove arrays that become empty after cleaning */
    removeEmptyArrays?: boolean
    /** Remove objects that become empty after cleaning */
    removeEmptyObjects?: boolean
    /** Additional values to remove beyond null/undefined/empty strings */
    additionalRemoveValues?: unknown[]
}

/**
 * Recursively removes null, undefined, and empty string values from an object.
 * Preserves arrays, numbers, booleans, and nested objects.
 *
 * @param data - The object to clean
 * @param options - Configuration options
 * @returns A new cleaned object
 *
 * @example
 * ```ts
 * cleanFilters({ name: 'John', age: null, address: { street: '' } })
 * // => { name: 'John', address: {} }
 * ```
 */
export const cleanFilters = <T extends Record<string, unknown>>(
    data: T,
    options: CleanFiltersOptions = {}
): Partial<T> => {
    const {removeEmptyArrays = false, removeEmptyObjects = false, additionalRemoveValues = []} = options

    const shouldRemove = (value: unknown): boolean => {
        // Base checks for null, undefined, and empty strings
        if (value == null || value === "") return true

        // Check against custom remove values
        return additionalRemoveValues.includes(value as never)
    }

    const deepClean = (input: unknown): unknown => {
        // Handle arrays
        if (Array.isArray(input)) {
            const cleanedArray = input.map(deepClean).filter(item => !shouldRemove(item))

            return removeEmptyArrays && cleanedArray.length === 0 ? undefined : cleanedArray
        }

        // Handle plain objects
        if (isPlainObject(input)) {
            const cleanedEntries = Object.entries(input).reduce<[string, unknown][]>((acc, [key, value]) => {
                const cleanedValue = deepClean(value)

                // Skip if the cleaned value should be removed
                if (shouldRemove(cleanedValue)) return acc

                acc.push([key, cleanedValue])
                return acc
            }, [])

            const result = Object.fromEntries(cleanedEntries)

            return removeEmptyObjects && Object.keys(result).length === 0 ? undefined : result
        }

        // Return primitives as-is
        return input
    }

    return deepClean(data) as Partial<T>
}

/**
 * Checks if an object is empty (has no own enumerable keys).
 * Useful as a companion to cleanFilters to determine if any filters remain.
 *
 * @param obj - The object to check
 * @returns true if the object has no keys, false otherwise
 *
 * @example
 * ```ts
 * const cleaned = cleanFilters({ name: '', age: null })
 * isEmpty(cleaned) // => true
 *
 * const cleaned2 = cleanFilters({ name: 'John', age: null })
 * isEmpty(cleaned2) // => false
 * ```
 */
export const isEmpty = (obj: Record<string, unknown>): boolean => {
    return Object.keys(obj).length === 0
}

/**
 * Type guard to check if value is a plain object
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        Object.getPrototypeOf(value) === Object.prototype
    )
}
